// Shared UI primitives — imported by all modules
import { useState, useEffect } from "react";

// Hook compartido: detecta viewport angosto (mobile/tablet)
export function useIsMobile(bp = 860) {
  const [m, setM] = useState(typeof window !== "undefined" && window.innerWidth < bp);
  useEffect(() => {
    const on = () => setM(window.innerWidth < bp);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, [bp]);
  return m;
}

export const C = {
  // Primarios agrícolas (mejorados para contraste)
  tierra:     "#1F120A",      // más oscuro para mejor contraste
  tierraMid:  "#4A3428",      // intermedio
  pasto:      "#2D5A34",      // verde tierra más saturado
  pastoLight: "#4A8052",      // luz más visible
  pradera:    "#7FB366",      // más saturado

  // Neutrales (mejor jerarquía)
  white:      "#FDFCF8",
  fondo:      "#F9F6ED",      // ligeramente más blanco
  arena:      "#EFE8D9",      // más saturado
  arenaDark:  "#D4CCC0",      // más visible

  // Texto (mejor contraste)
  texto:      "#1A1A18",      // negro profundo
  muted:      "#5F5F55",      // más oscuro para contraste WCAG AA
  mutedLight: "#8C8C7E",      // para elementos menos importantes

  // Estados
  activo:     "#2D5A34",      // = pasto (coherencia)
  descanso:   "#B8943A",      // dorado cálido (sin cambios)
  disponible: "#3D7FB5",      // azul más saturado
  sembrado:   "#C56B34",      // naranja tierra (más saturado)

  // Feedback
  ok:         "#28641F",      // verde oscuro
  okLight:    "#4FAC4C",      // verde claro para backgrounds
  warn:       "#8B6400",      // naranja-marrón oscuro
  warnLight:  "#FFD966",      // amarillo visible
  danger:     "#8B1F1F",      // rojo profundo
  dangerLight:"#D97575",      // rojo claro para backgrounds
};

export function uid() { return Date.now().toString(36) + Math.random().toString(36).slice(2,6); }

export function fmt(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("es-UY", { day:"2-digit", month:"2-digit", year:"2-digit" });
}

export function pesoActual(animal) {
  const ps = animal.pesadas?.filter(p => p && p.kg > 0);
  if (!ps?.length) return animal.pesoInicial || 0;
  return [...ps].sort((a,b) => a.fecha.localeCompare(b.fecha)).at(-1).kg;
}

export function gananciaTotal(animal) {
  const pa = pesoActual(animal);
  const pi = animal.pesoInicial || 0;
  if (!pa || !pi) return 0;
  return Math.round((pa - pi) * 10) / 10;
}

export function Badge({ color, children }) {
  const bg = { activo:"#D4E8D4", descanso:"#FFF0C0", disponible:"#D0E4F4", sembrado:"#FAE0D0" };
  const tx = { activo:C.ok, descanso:C.warn, disponible:"#1E5080", sembrado:"#7A3020" };
  return (
    <span style={{ fontFamily:"monospace", fontSize:10, padding:"2px 8px", borderRadius:20,
      background:bg[color]||"#eee", color:tx[color]||"#333", whiteSpace:"nowrap" }}>
      {children}
    </span>
  );
}

export function Btn({ onClick, variant="primary", size="md", disabled, children, style={} }) {
  const base = {
    border:"none", borderRadius:9, fontFamily:"sans-serif", fontWeight:600,
    cursor:disabled?"not-allowed":"pointer", opacity:disabled?0.55:1,
    transition:"all 0.2s ease", letterSpacing:"0.02em",
    ...style
  };
  const v = {
    primary:   {
      background:C.pasto, color:"white", padding:size==="sm"?"7px 14px":"12px 18px", fontSize:size==="sm"?12:14,
      boxShadow:"0 2px 4px rgba(45,90,52,0.15)"
    },
    secondary: {
      background:"transparent", color:C.pasto, border:`1.5px solid ${C.pasto}`,
      padding:size==="sm"?"6px 13px":"11px 17px", fontSize:size==="sm"?12:14
    },
    ghost:     {
      background:"transparent", color:C.muted, padding:size==="sm"?"6px 12px":"10px 16px", fontSize:size==="sm"?12:14
    },
    danger:    {
      background:C.dangerLight, color:"white", padding:size==="sm"?"7px 14px":"12px 18px", fontSize:size==="sm"?12:14,
      boxShadow:"0 2px 4px rgba(139,31,31,0.15)"
    },
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{...base,...v[variant]}}
      onMouseEnter={e => {
        if (!disabled) {
          if (variant === "primary") e.target.style.transform = "translateY(-1px)";
          if (variant === "secondary") e.target.style.opacity = "0.8";
        }
      }}
      onMouseLeave={e => {
        if (!disabled) {
          e.target.style.transform = "translateY(0)";
          e.target.style.opacity = "1";
        }
      }}
    >
      {children}
    </button>
  );
}

export function Input({ label, value, onChange, type="text", placeholder, required }) {
  return (
    <div style={{ marginBottom:14 }}>
      {label && <div style={{ fontSize:12, fontFamily:"monospace", textTransform:"uppercase", letterSpacing:"0.08em", color:C.muted, marginBottom:6, fontWeight:500 }}>{label}{required&&<span style={{color:C.danger}}> *</span>}</div>}
      <input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
        style={{ width:"100%", padding:"10px 12px", border:`1.5px solid ${C.arenaDark}`, borderRadius:8,
          fontFamily:"sans-serif", fontSize:14, background:C.arena, color:C.texto, outline:"none", boxSizing:"border-box",
          transition:"all 0.2s", cursor:"text" }}
        onFocus={e=>e.target.style.borderColor=C.pasto}
        onBlur={e=>e.target.style.borderColor=C.arenaDark}/>
    </div>
  );
}

export function Select({ label, value, onChange, options, required }) {
  return (
    <div style={{ marginBottom:14 }}>
      {label && <div style={{ fontSize:12, fontFamily:"monospace", textTransform:"uppercase", letterSpacing:"0.08em", color:C.muted, marginBottom:6, fontWeight:500 }}>{label}{required&&<span style={{color:C.danger}}> *</span>}</div>}
      <select value={value} onChange={e=>onChange(e.target.value)}
        style={{ width:"100%", padding:"10px 12px", border:`1.5px solid ${C.arenaDark}`, borderRadius:8,
          fontFamily:"sans-serif", fontSize:14, background:C.arena, color:C.texto, outline:"none", cursor:"pointer",
          transition:"all 0.2s" }}
        onFocus={e=>e.target.style.borderColor=C.pasto}
        onBlur={e=>e.target.style.borderColor=C.arenaDark}>
        {options.map(o => typeof o==="string" ? <option key={o} value={o}>{o}</option> : <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}

export function Textarea({ label, value, onChange, rows=3, placeholder }) {
  return (
    <div style={{ marginBottom:14 }}>
      {label && <div style={{ fontSize:12, fontFamily:"monospace", textTransform:"uppercase", letterSpacing:"0.08em", color:C.muted, marginBottom:6, fontWeight:500 }}>{label}</div>}
      <textarea value={value} onChange={e=>onChange(e.target.value)} rows={rows} placeholder={placeholder}
        style={{ width:"100%", padding:"10px 12px", border:`1.5px solid ${C.arenaDark}`, borderRadius:8,
          fontFamily:"sans-serif", fontSize:14, background:C.arena, color:C.texto, outline:"none",
          resize:"vertical", boxSizing:"border-box", transition:"all 0.2s" }}
        onFocus={e=>e.target.style.borderColor=C.pasto}
        onBlur={e=>e.target.style.borderColor=C.arenaDark}/>
    </div>
  );
}

export function Modal({ title, onClose, children, width=400 }) {
  return (
    <div onClick={onClose}
      style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", display:"flex",
      alignItems:"center", justifyContent:"center", zIndex:9999, padding:16, overflowY:"auto" }}>
      <div onClick={e=>e.stopPropagation()}
        style={{ background:C.white, borderRadius:16, padding:24, width:"100%", maxWidth:width,
        maxHeight:"90vh", overflowY:"auto" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
          <div style={{ fontFamily:"serif", fontSize:18, fontWeight:600, color:C.tierra }}>{title}</div>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", fontSize:20, color:C.muted, lineHeight:1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Section({ label, children }) {
  return (
    <div style={{ padding:"16px", borderBottom:`1px solid ${C.arenaDark}` }}>
      {label && <div style={{ fontSize:10, fontFamily:"monospace", textTransform:"uppercase", letterSpacing:"0.1em", color:C.muted, marginBottom:12 }}>{label}</div>}
      {children}
    </div>
  );
}

// Sección colapsable: encabezado clickeable con chevron. `defaultOpen` controla
// el estado inicial. `badge` muestra un contador opcional a la derecha.
export function CollapsibleSection({ label, children, defaultOpen=true, badge }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderBottom:`1px solid ${C.arenaDark}` }}>
      <button onClick={()=>setOpen(o=>!o)}
        style={{ width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between",
          background:"none", border:"none", cursor:"pointer", padding:"14px 16px", textAlign:"left" }}>
        <span style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:10, fontFamily:"monospace", textTransform:"uppercase",
            letterSpacing:"0.1em", color:C.muted }}>{label}</span>
          {badge!=null && badge!=="" && (
            <span style={{ fontSize:10, fontWeight:700, color:C.pasto, background:C.pastoLight+"22",
              borderRadius:10, padding:"1px 7px", fontFamily:"monospace" }}>{badge}</span>
          )}
        </span>
        <span style={{ fontSize:11, color:C.muted, transform:open?"rotate(90deg)":"none",
          transition:"transform 0.15s" }}>▸</span>
      </button>
      {open && <div style={{ padding:"0 16px 16px" }}>{children}</div>}
    </div>
  );
}

export function StatCard({ val, label, sub, color }) {
  return (
    <div style={{ background:C.arena, borderRadius:9, padding:"10px 13px" }}>
      <div style={{ fontFamily:"serif", fontSize:22, fontWeight:600, color:color||C.tierra, lineHeight:1 }}>{val}</div>
      <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>{label}</div>
      {sub && <div style={{ fontSize:10, color:C.muted, marginTop:1, fontFamily:"monospace" }}>{sub}</div>}
    </div>
  );
}

// Edad legible desde fecha de nacimiento
export function calcEdad(fechaNac) {
  if (!fechaNac) return null;
  const nac  = new Date(fechaNac);
  const hoy  = new Date();
  const meses = (hoy.getFullYear() - nac.getFullYear()) * 12 + (hoy.getMonth() - nac.getMonth());
  if (meses < 1)  return "< 1 mes";
  if (meses < 24) return `${meses} mes${meses !== 1 ? "es" : ""}`;
  const a = Math.floor(meses / 12), m = meses % 12;
  return m > 0 ? `${a} a ${m}m` : `${a} año${a !== 1 ? "s" : ""}`;
}

// ─── CATÁLOGOS CENTRALIZADOS (para evitar texto libre y typos) ───
export const ESPECIES = ["Bovino", "Ovino", "Caballo"];

export const ICONO_ESP = { Bovino: "🐄", Ovino: "🐑", Caballo: "🐎" };

export const CATEGORIAS_ANIMAL = {
  Bovino:  ["Vaca de cría","Novillo","Novillito","Ternero/a","Vaquillona","Toro","Buey"],
  Ovino:   ["Oveja","Borrego/a","Carnero","Capón","Cordero/a"],
  Caballo: ["Yegua","Padrillo","Potrillo/a","Caballo de andar","Petiso"],
};

// Razas predefinidas por especie
export const RAZAS = {
  Bovino:  ["Hereford","Aberdeen Angus","Braford","Shorthorn","Holando","Limousine","Simmental","Charolais","Cruza"],
  Ovino:   ["Corriedale","Merino","Romney Marsh","Ideal","Polwarth","Hampshire","Lincoln","Texel","Dohne","Cruza"],
  Caballo: ["Criollo","Cuarto de Milla","Pura Sangre","Árabe","Percherón","Polo Argentino","Cruza"],
};

// Semillas / pasturas frecuentes (para actividades de siembra y enfardado)
export const SEMILLAS_OPC = [
  "Raigrás anual","Raigrás Acreman","Festuca","Trébol blanco","Trébol rojo",
  "Lotus","Alfalfa","Avena","Moha","Sorgo forrajero","Maíz","Soja","Trigo",
  "Cebada","Pradera mezcla","Campo natural",
];

// Helper: devuelve las categorías de una especie (con fallback)
export const catsDe = (especie) => CATEGORIAS_ANIMAL[especie] || CATEGORIAS_ANIMAL.Bovino;

// Selector de raza (dropdown estricto). Si el valor actual no está en la lista
// (datos viejos), lo agrega como opción para no perderlo.
export function RazaInput({ label, value, onChange, especie }) {
  const base = RAZAS[especie] || RAZAS.Bovino;
  const opciones = value && !base.includes(value) ? [value, ...base] : base;
  return (
    <Select
      label={label}
      value={value || opciones[0]}
      onChange={onChange}
      options={opciones}
    />
  );
}

export function Tabs({ tabs, active, onChange }) {
  return (
    <div style={{ display:"flex", gap:4, borderBottom:`1px solid ${C.arenaDark}`, marginBottom:20 }}>
      {tabs.map(t=>(
        <button key={t.id} onClick={()=>onChange(t.id)} style={{
          padding:"7px 14px", fontSize:12, fontFamily:"monospace", border:"none", cursor:"pointer",
          background:"transparent", color:active===t.id ? C.pasto : C.muted,
          borderBottom:active===t.id ? `2px solid ${C.pasto}` : "2px solid transparent",
          fontWeight:active===t.id ? 600 : 400, textTransform:"uppercase", letterSpacing:"0.06em"
        }}>{t.label}</button>
      ))}
    </div>
  );
}

// Autocomplete de producto: datalist + input con búsqueda
export function ProductoInput({ label, value, onChange, productos=[], desc="" }) {
  const listId = `productos-${Math.random().toString(36).slice(2,9)}`;
  return (
    <div style={{ marginBottom:12 }}>
      {label && <div style={{ fontSize:11, fontFamily:"monospace", textTransform:"uppercase", letterSpacing:"0.08em", color:C.muted, marginBottom:4 }}>{label}</div>}
      <input
        list={listId}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="Escribí para buscar..."
        style={{ width:"100%", padding:"8px 10px", border:`1.5px solid ${C.arenaDark}`, borderRadius:7,
          fontFamily:"sans-serif", fontSize:13, background:C.arena, color:C.texto, outline:"none", boxSizing:"border-box" }}
      />
      {desc && <div style={{ fontSize:10, color:C.muted, marginTop:3, fontStyle:"italic" }}>{desc}</div>}
      <datalist id={listId}>
        {productos.map((p,i) => <option key={i} value={typeof p==="string" ? p : p.nombre}/>)}
      </datalist>
    </div>
  );
}

// Mini-map SVG de un potrero (simple static view)
export function PotreroMiniMap({ potrero, height=180 }) {
  if (!potrero?.points?.length) return null;
  const points = potrero.points;
  const lats = points.map(p => p[0]);
  const lngs = points.map(p => p[1]);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
  const padLat = (maxLat - minLat) * 0.15 || 0.01;
  const padLng = (maxLng - minLng) * 0.15 || 0.01;
  const viewLat = [minLat - padLat, maxLat + padLat];
  const viewLng = [minLng - padLng, maxLng + padLng];
  const w = 280, h = height;
  const toX = (lng) => ((lng - viewLng[0]) / (viewLng[1] - viewLng[0])) * w;
  const toY = (lat) => ((viewLat[1] - lat) / (viewLat[1] - viewLat[0])) * h;
  const pathD = points.map((p,i) => `${i===0?"M":"L"} ${toX(p[1])} ${toY(p[0])}`).join(" ") + " Z";
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} style={{ background:"#E8F4F0", borderRadius:8, border:`1px solid ${C.arenaDark}` }}>
      <path d={pathD} fill={potrero.color} opacity="0.5" stroke={potrero.color} strokeWidth="2"/>
      {points.map((p,i) => (
        <circle key={i} cx={toX(p[1])} cy={toY(p[0])} r="3" fill={C.tierra}/>
      ))}
      <text x="6" y="16" fontSize="11" fill={C.tierra} fontWeight="600">{potrero.nombre}</text>
      <text x="6" y="30" fontSize="9" fill={C.muted}>{potrero.ha.toFixed(1)} ha · {potrero.pasto}</text>
    </svg>
  );
}

// Simple bar chart component
export function BarChart({ data, height=120, color=C.pasto }) {
  if (!data?.length) return null;
  const max = Math.max(...data.map(d => d.value));
  const w = 280;
  const barW = w / data.length;
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${w} ${height}`} style={{ borderRadius:6 }}>
      {data.map((d,i) => {
        const h = (d.value / max) * (height - 30);
        const x = i * barW + barW * 0.1;
        const y = height - h - 20;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW * 0.8} height={h} fill={color} opacity="0.7" rx="2"/>
            <text x={x + barW * 0.4} y={height - 5} fontSize="10" textAnchor="middle" fill={C.muted}>{d.label}</text>
            {d.value > 0 && <text x={x + barW * 0.4} y={y - 4} fontSize="10" textAnchor="middle" fill={C.tierra} fontWeight="600">{d.value}</text>}
          </g>
        );
      })}
    </svg>
  );
}
